import type { EventConfig } from '@/types'
import { portableEvent } from '../../server/lib/eventSettings'
import { generateShareToken, calculateExpiration } from './utils'

/**
 * Give an imported event its own identity.
 *
 * Two things must not be inherited from the source event: its database id, and
 * its share tokens. Reusing a publicToken would leave two events answering to
 * the same public gallery link, so tokens are always regenerated and the expiry
 * window restarts from the import. Replacing an existing event keeps *that*
 * event's id and tokens instead, so links already handed out keep working.
 */
export function reidentifyImportedConfig(
  config: EventConfig,
  opts: { id: string; name: string; keepTokensFrom?: EventConfig }
): EventConfig {
  const now = new Date().toISOString()
  const next: EventConfig = portableEvent(JSON.parse(JSON.stringify(config)))

  next.id = opts.id
  next.name = opts.name
  next.createdAt = opts.keepTokensFrom?.createdAt || now
  next.updatedAt = now

  const freshToken = (
    existing: string | null | undefined,
    inherited: string | null | undefined
  ) => (opts.keepTokensFrom ? existing ?? null : inherited ? generateShareToken() : null)

  if (next.gallerySettings) {
    const g = next.gallerySettings
    g.publicToken = freshToken(opts.keepTokensFrom?.gallerySettings?.publicToken, g.publicToken)
    if (!opts.keepTokensFrom && g.publicAccess === 'enabled') {
      // Restart the window: an absolute expiry copied from the source event
      // could already be in the past.
      g.publicAccessCreatedAt = now
      g.publicAccessExpiresAt = calculateExpiration(
        g.publicAccessDuration || 'permanent',
        g.publicAccessDurationValue || 1
      )
    }
  }

  if (next.kiosk) {
    const k = next.kiosk
    k.publicToken = freshToken(opts.keepTokensFrom?.kiosk?.publicToken, k.publicToken)
    if (!opts.keepTokensFrom && k.publicAccess === 'enabled') {
      k.publicAccessCreatedAt = now
      k.publicAccessExpiresAt = calculateExpiration(
        k.publicAccessDuration || 'permanent',
        k.publicAccessDurationValue || 1
      )
    }
  }

  return next
}

